// ─── CRON parsing / validation ──────────────────────────────────────────────
// Standard 5-field cron:  minute  hour  day-of-month  month  day-of-week
// Dependency-free. Validates field syntax + ranges and produces a human
// readable sentence (e.g. "Runs every Monday at 08:30 PM").

const DOW_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const FIELD_RANGES = {
    minute: [0, 59],
    hour: [0, 23],
    'day of month': [1, 31],
    month: [1, 12],
    'day of week': [0, 7], // 0 and 7 both mean Sunday
};

// Validate a single cron field (supports *, lists, ranges, and steps).
const isFieldValid = (field, [min, max]) => {
    if (field === '*') return true;

    return field.split(',').every((part) => {
        if (part === '') return false;

        // Step syntax:  */n  or  a-b/n  or  a/n
        let range = part;
        let step = null;
        if (part.includes('/')) {
            const [rangePart, stepPart] = part.split('/');
            if (stepPart === '' || !/^\d+$/.test(stepPart) || Number(stepPart) === 0) return false;
            range = rangePart;
            step = Number(stepPart);
        }

        if (range === '*') return true;

        if (range.includes('-')) {
            const [a, b] = range.split('-');
            if (!/^\d+$/.test(a) || !/^\d+$/.test(b)) return false;
            const start = Number(a);
            const end = Number(b);
            return start >= min && end <= max && start <= end;
        }

        if (!/^\d+$/.test(range)) return false;
        const val = Number(range);
        // A bare number with a step (e.g. "5/10") is treated as "from 5 onwards".
        return val >= min && val <= max && (step === null || true);
    });
};

// Returns { valid, error?, fields? }
export const validateCron = (expression) => {
    if (!expression || !expression.trim()) {
        return { valid: false, error: 'CRON expression is required' };
    }

    const parts = expression.trim().split(/\s+/);
    if (parts.length !== 5) {
        return { valid: false, error: 'CRON must have exactly 5 fields (min hour day month weekday)' };
    }

    const [minute, hour, dom, month, dow] = parts;
    const checks = [
        ['minute', minute],
        ['hour', hour],
        ['day of month', dom],
        ['month', month],
        ['day of week', dow],
    ];

    for (const [name, value] of checks) {
        if (!isFieldValid(value, FIELD_RANGES[name])) {
            return { valid: false, error: `Invalid value "${value}" in the ${name} field` };
        }
    }

    return { valid: true, fields: { minute, hour, dom, month, dow } };
};

// ── Readable-sentence helpers ────────────────────────────────────────────────

const format12h = (hour, minute) => {
    const h = Number(hour);
    const m = Number(minute);
    const meridiem = h < 12 ? 'AM' : 'PM';
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    const mm = String(m).padStart(2, '0');
    return `${String(h12).padStart(2, '0')}:${mm} ${meridiem}`;
};

// Expand a single-value list (no ranges/steps) into a readable phrase using a
// name lookup; returns null if the field isn't a simple enumerable list.
const namedList = (field, mapper) => {
    if (field === '*') return null;
    if (/[*/-]/.test(field)) return null;
    const items = field.split(',').map((n) => mapper(Number(n))).filter(Boolean);
    if (items.length === 0) return null;
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

// Produces { valid, text, error } — text is the human-readable description.
export const describeCron = (expression) => {
    const result = validateCron(expression);
    if (!result.valid) return { valid: false, text: '', error: result.error };

    const { minute, hour, dom, month, dow } = result.fields;
    const parts = [];

    const minuteIsExact = /^\d+$/.test(minute);
    const hourIsExact = /^\d+$/.test(hour);

    // ── Time-of-day clause ──
    let timeClause = '';
    if (minuteIsExact && hourIsExact) {
        timeClause = `at ${format12h(hour, minute)}`;
    } else if (minute === '*' && hour === '*') {
        timeClause = 'every minute';
    } else if (minuteIsExact && hour === '*') {
        timeClause = `at minute ${Number(minute)} of every hour`;
    } else if (minute.startsWith('*/')) {
        timeClause = `every ${minute.slice(2)} minutes`;
    } else {
        timeClause = `at minute ${minute}, hour ${hour}`;
    }

    // ── Day-of-week / day-of-month clause ──
    let dayClause = 'every day';
    const normalizedDow = dow === '7' ? '0' : dow;
    const dowNamed = namedList(normalizedDow, (n) => DOW_NAMES[n % 7]);
    const domIsAll = dom === '*';
    const dowIsAll = dow === '*';

    if (!dowIsAll && normalizedDow === '1-5') {
        dayClause = 'every weekday';
    } else if (!dowIsAll && (normalizedDow === '0,6' || normalizedDow === '6,0' || dow === '6,7')) {
        dayClause = 'every weekend';
    } else if (!dowIsAll && /^\d+-\d+$/.test(dow)) {
        const [a, b] = dow.split('-').map(Number);
        dayClause = `every ${DOW_NAMES[a % 7]} through ${DOW_NAMES[b % 7]}`;
    } else if (!dowIsAll && dowNamed) {
        dayClause = `every ${dowNamed}`;
    } else if (!domIsAll && /^\d+(,\d+)*$/.test(dom)) {
        const domList = namedList(dom, (n) => `the ${ordinal(n)}`);
        dayClause = domList ? `on ${domList}` : 'every day';
    }

    // ── Month clause ──
    let monthClause = '';
    if (month !== '*') {
        const monthNamed = namedList(month, (n) => MONTH_NAMES[n]);
        if (monthNamed) monthClause = `in ${monthNamed}`;
    }

    // Assemble: "Runs <day> <time> <month>"
    if (timeClause === 'every minute' || timeClause.startsWith('every ')) {
        // frequency-style clause reads better first
        parts.push(`Runs ${timeClause}`);
        if (dayClause !== 'every day') parts.push(dayClause);
    } else {
        parts.push(`Runs ${dayClause}`, timeClause);
    }
    if (monthClause) parts.push(monthClause);

    return { valid: true, text: parts.join(' ').replace(/\s+/g, ' ').trim(), error: '' };
};

const ordinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};